import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileInput } from "@/components/FileInput";
import { DateCell } from "@/components/DateCell";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Plus, Trash2, Loader2, Check, CalendarClock, FileText, FileSpreadsheet, Info } from "lucide-react";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const TOTAL_ID = "__total__";
const cell = "h-8 w-full bg-transparent border border-transparent hover:border-slate-300 rounded-md px-2 text-sm transition-colors focus:outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary/20";
const cellNum = cell + " text-right tabular-nums";

const Spinner = () => <div className="flex justify-center py-16"><div className="h-7 w-7 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

export default function PaymentSchedule({ projectId, access = "view" }) {
  const ro = access === "view";
  const [rows, setRows] = useState([]);
  const [partidas, setPartidas] = useState([]);
  const [budgetTotal, setBudgetTotal] = useState(0);
  const [validatedAt, setValidatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("saved");
  const [validating, setValidating] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const loadedRef = useRef(false);
  const timerRef = useRef(null);

  const reload = () => api.get(`/projects/${projectId}/payment-schedule`).then((r) => {
    setRows(r.data.rows || []);
    setPartidas(r.data.partidas || []);
    setBudgetTotal(r.data.budget_total || 0);
    setValidatedAt(r.data.validated_at || null);
  });

  useEffect(() => {
    let alive = true;
    api.get(`/projects/${projectId}/payment-schedule`).then((r) => {
      if (!alive) return;
      setRows(r.data.rows || []);
      setPartidas(r.data.partidas || []);
      setBudgetTotal(r.data.budget_total || 0);
      setValidatedAt(r.data.validated_at || null);
      setLoading(false);
      setTimeout(() => { loadedRef.current = true; }, 120);
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  useEffect(() => {
    if (!loadedRef.current || ro) return;
    setSaveState("dirty");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSaveState("saving");
      try { await api.put(`/projects/${projectId}/payment-schedule`, { rows }); setSaveState("saved"); }
      catch (e) { setSaveState("dirty"); }
    }, 1000);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const pmap = useMemo(() => Object.fromEntries(partidas.map((p) => [p.id, p])), [partidas]);
  const lineTotalOf = (row) => row.line_id === TOTAL_ID ? budgetTotal : (pmap[row.line_id]?.total || 0);
  const baseOf = (row) => Math.round((Number(row.pct) || 0) / 100 * lineTotalOf(row) * 100) / 100;

  const setRow = (i, patch) => setRows((p) => p.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows((p) => [...p, { id: uid(), line_id: TOTAL_ID, label: "", pct: 0, date: "" }]);
  const delRow = (i) => setRows((p) => p.filter((_, idx) => idx !== i));

  const totalBase = rows.reduce((a, r) => a + baseOf(r), 0);
  const totalIva = totalBase * 1.21;
  const pctBudget = budgetTotal > 0 ? Math.round(totalBase / budgetTotal * 1000) / 10 : 0;

  const validate = async () => {
    if (!window.confirm("¿Validar el cronograma? Se generarán/actualizarán las facturas previstas (estado «Pendiente de emisión», IVA 21%) en la pestaña Ingresos.")) return;
    setValidating(true);
    try {
      // fuerza guardar lo pendiente antes de validar
      await api.put(`/projects/${projectId}/payment-schedule`, { rows });
      const r = await api.post(`/projects/${projectId}/payment-schedule/validate`);
      const { created = 0, updated = 0, skipped = 0, removed = 0 } = r.data || {};
      setValidatedAt(new Date().toISOString());
      setSaveState("saved");
      toast.success(`Cronograma validado · ${created} creadas, ${updated} actualizadas${removed ? `, ${removed} eliminadas` : ""}${skipped ? ` · ${skipped} respetadas (ya emitidas/cobradas)` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo validar"); }
    finally { setValidating(false); }
  };

  if (loading) return <Spinner />;

  if (partidas.length === 0) {
    return (
      <>
      <Card className="border-border border-dashed shadow-none py-12 text-center" data-testid="payment-schedule-empty">
        <CalendarClock className="h-8 w-8 text-muted-foreground/50 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          El cronograma de pago funciona con el <b>presupuesto simplificado</b> (Dining / OnStage).
          Crea primero el presupuesto del proyecto con partidas e importes para poder programar los cobros,
          o <b>importa directamente el cronograma desde Excel</b>.
        </p>
        {!ro && <Button size="sm" variant="outline" className="gap-1.5 mt-4" onClick={() => setImportOpen(true)} data-testid="schedule-import-btn-empty"><FileSpreadsheet className="h-4 w-4" /> Importar cronograma (Excel)</Button>}
      </Card>
      <ScheduleImportDialog projectId={projectId} open={importOpen} onOpenChange={setImportOpen} existingCount={rows.length} onDone={async () => { await reload(); }} />
      </>
    );
  }

  return (
    <div className="space-y-4" data-testid="payment-schedule">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge className="bg-navy/10 text-navy border-transparent gap-1"><CalendarClock className="h-3 w-3" /> Cronograma de pago</Badge>
          {!ro && (
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
              {saveState === "saving" ? <><Loader2 className="h-3 w-3 animate-spin" /> Guardando…</> : saveState === "dirty" ? "Sin guardar" : <><Check className="h-3 w-3 text-primary" /> Guardado</>}
            </span>
          )}
          {validatedAt && <span className="text-xs text-muted-foreground">Validado {fmtDate(validatedAt)}</span>}
        </div>
        {!ro && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setImportOpen(true)} data-testid="schedule-import-btn"><FileSpreadsheet className="h-4 w-4" /> Importar Excel</Button>
            <Button size="sm" className="gap-1.5" onClick={validate} disabled={validating} data-testid="schedule-validate-btn">
              {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Validar y generar facturas
            </Button>
          </div>
        )}
      </div>

      <div className="text-xs rounded-md bg-navy/[0.04] text-muted-foreground px-3 py-2">
        Elige una <b>partida</b> del presupuesto (o el total) y un <b>%</b>: el importe se calcula solo. Al validar, cada línea crea una factura prevista en <b>Ingresos</b> con estado «Pendiente de emisión» e IVA 21%.
      </div>

      <Card className="border-border shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left border-b border-border bg-muted/20">
                <th className="px-2 py-2 font-semibold w-[30%]">Partida del presupuesto</th>
                <th className="px-2 py-2 font-semibold text-right w-20">%</th>
                <th className="px-2 py-2 font-semibold text-right w-28">Base</th>
                <th className="px-2 py-2 font-semibold text-right w-28">Total c/IVA</th>
                <th className="px-2 py-2 font-semibold w-36">Fecha de cobro</th>
                <th className="px-2 py-2 font-semibold w-[24%]">Concepto factura</th>
                <th className="px-2 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const base = baseOf(r);
                return (
                  <tr key={r.id} className="border-b border-border/60 last:border-0 hover:bg-muted/20 transition-colors" data-testid={`schedule-row-${i}`}>
                    <td className="p-1">
                      <select className={cell} value={r.line_id || TOTAL_ID} disabled={ro} onChange={(e) => setRow(i, { line_id: e.target.value })} data-testid={`schedule-line-${i}`}>
                        <option value={TOTAL_ID}>Total del presupuesto · {eur(budgetTotal)}</option>
                        {partidas.map((p) => (
                          <option key={p.id} value={p.id}>{p.concept} · {eur(p.total)}{p.section ? ` (${p.section})` : ""}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-1"><input type="number" className={cellNum} value={r.pct} disabled={ro} onChange={(e) => setRow(i, { pct: e.target.value })} data-testid={`schedule-pct-${i}`} /></td>
                    <td className="p-1 text-right tabular-nums font-medium whitespace-nowrap" data-testid={`schedule-base-${i}`}>{eur(base)}</td>
                    <td className="p-1 text-right tabular-nums text-muted-foreground whitespace-nowrap">{eur(base * 1.21)}</td>
                    <td className="p-1"><DateCell value={r.date || ""} disabled={ro} onChange={(v) => setRow(i, { date: v })} testid={`schedule-date-${i}`} placeholder="Fecha cobro…" /></td>
                    <td className="p-1"><input className={cell} value={r.label || ""} disabled={ro} placeholder={r.line_id === TOTAL_ID ? "Ej: 50% inicio" : (pmap[r.line_id]?.concept || "Concepto")} onChange={(e) => setRow(i, { label: e.target.value })} data-testid={`schedule-label-${i}`} /></td>
                    <td className="p-1">{!ro && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => delRow(i)} data-testid={`schedule-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center text-xs text-muted-foreground">Sin cobros programados. Añade una línea para empezar.</td></tr>}
            </tbody>
          </table>
        </div>
        {!ro && <div className="px-3 py-2 border-t border-border"><Button size="sm" variant="ghost" className="text-primary gap-1.5" onClick={addRow} data-testid="schedule-add-row"><Plus className="h-4 w-4" /> Añadir cobro</Button></div>}
      </Card>

      <div className="flex flex-wrap items-center justify-end gap-6 text-right">
        <div>
          <div className="text-xs text-muted-foreground">Total presupuesto</div>
          <div className="text-base font-heading font-bold tabular-nums">{eur(budgetTotal)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Programado (base)</div>
          <div className={cn("text-base font-heading font-bold tabular-nums", pctBudget > 100 && "text-destructive")} data-testid="schedule-total-base">{eur(totalBase)} <span className="text-xs font-normal text-muted-foreground">({pctBudget}%)</span></div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Programado c/IVA</div>
          <div className="text-2xl font-heading font-bold tabular-nums" data-testid="schedule-total-iva">{eur(totalIva)}</div>
        </div>
      </div>

      <ScheduleImportDialog projectId={projectId} open={importOpen} onOpenChange={setImportOpen} existingCount={rows.length} onDone={async () => { await reload(); }} />
    </div>
  );
}

function ScheduleImportDialog({ projectId, open, onOpenChange, existingCount, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("replace");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [importing, setImporting] = useState(false);
  const hasExisting = (existingCount || 0) > 0;

  const reset = () => { setFile(null); setPreview(null); setMode("replace"); setLoadingPreview(false); setImporting(false); };

  const onPick = async (f) => {
    setFile(f); setPreview(null);
    if (!f) return;
    setLoadingPreview(true);
    try {
      const fd = new FormData();
      fd.append("file", f); fd.append("apply", "false");
      const { data } = await api.post(`/projects/${projectId}/payment-schedule/import-excel`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo leer el Excel del cronograma.");
      setFile(null);
    } finally { setLoadingPreview(false); }
  };

  const doImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("apply", "true"); fd.append("mode", hasExisting ? mode : "replace");
      const { data } = await api.post(`/projects/${projectId}/payment-schedule/import-excel`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { created = 0, updated = 0, removed = 0, skipped = 0 } = data || {};
      toast.success(`Cronograma importado · ${created} creadas, ${updated} actualizadas${removed ? `, ${removed} eliminadas` : ""}${skipped ? ` · ${skipped} respetadas` : ""}`);
      await onDone();
      onOpenChange(false); reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo importar el cronograma.");
    } finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg" data-testid="schedule-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-primary" /> Importar cronograma desde Excel</DialogTitle>
          <DialogDescription>Sube el Excel del calendario de pagos (fechas, conceptos e importes o %). Al importar se validan y se generan las facturas previstas en Ingresos.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <FileInput file={file} onFile={onPick} accept=".xlsx,.xls" testid="schedule-import-file" />

          {loadingPreview && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Leyendo Excel…</div>}

          {preview && (
            <div className="rounded-md border border-border bg-muted/20 p-3 text-sm space-y-2" data-testid="schedule-import-preview">
              <div className="flex items-center gap-2 text-primary font-medium"><Info className="h-4 w-4" /> Vista previa · {preview.count} cobros</div>
              <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                {(preview.rows || []).slice(0, 12).map((r, idx) => (
                  <li key={idx} className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span className="truncate">{r.label || "Cobro"}{r.date ? ` · ${fmtDate(r.date)}` : ""}</span>
                    <span className="tabular-nums shrink-0">{r.amount != null ? eur(r.amount) : `${r.pct}%`}</span>
                  </li>
                ))}
                {(preview.rows || []).length > 12 && <li className="text-xs text-muted-foreground/70">…y {preview.rows.length - 12} más</li>}
              </ul>
            </div>
          )}

          {preview && hasExisting && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm space-y-2" data-testid="schedule-import-mode">
              <div className="font-medium text-amber-800">Ya hay un cronograma con {existingCount} cobros. ¿Qué quieres hacer?</div>
              <label className="flex items-start gap-2 cursor-pointer" data-testid="schedule-mode-replace">
                <input type="radio" name="sched-mode" className="mt-1 accent-navy" checked={mode === "replace"} onChange={() => setMode("replace")} />
                <span><b>Reemplazar</b> el cronograma actual. Las facturas ya emitidas o cobradas se conservan; las pendientes de emisión se regeneran con el Excel nuevo.</span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer" data-testid="schedule-mode-merge">
                <input type="radio" name="sched-mode" className="mt-1 accent-navy" checked={mode === "merge"} onChange={() => setMode("merge")} />
                <span><b>Añadir</b> los cobros del Excel al cronograma actual (se conservan los existentes).</span>
              </label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); reset(); }} data-testid="schedule-import-cancel">Cancelar</Button>
          <Button onClick={doImport} disabled={!preview || importing} data-testid="schedule-import-confirm">
            {importing ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Importando…</> : "Importar cronograma"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
